/**
 * Pure feeding arithmetic shared by the server, the web and the tests: weight conversion, ration
 * mixes (as-fed and dry-matter basis), head ledgers and head days, pen-to-lot allocation and
 * closeout performance. No DOM, no Worker APIs. Every function says what it cannot compute rather
 * than guessing a value.
 */
import { addDays, convertUnit, daysBetween, type Quantity } from "../../../contract";

/** US short ton, the "ton" in feed prices. A metric tonne ("t") is a different unit. */
export const LB_PER_TON = 2000;
export const WEIGHT_UNITS = ["lb", "kg", "ton", "t", "oz", "g"] as const;

export const round = (n: number, digits = 2): number => {
  const f = 10 ** digits;
  return Math.round(n * f) / f;
};

/** Pounds in a weight quantity, or null when the unit is not a weight. */
export function toLb(q: Quantity | null | undefined): number | null {
  if (!q || typeof q.value !== "number") return null;
  return convertUnit(q.value, q.unit, "lb");
}

export const isWeightUnit = (unit: string): boolean => (WEIGHT_UNITS as readonly string[]).includes(unit);

// ---------------------------------------------------------------- rations

export type RationBasis = "as_fed" | "dry_matter";

/** One ingredient as entered for a ration version. Percentages are in the version's basis. */
export interface IngredientInput {
  item_id: string;
  name: string;
  /** The product's stock unit (lb, ton, bale, bag...). */
  unit: string;
  pct: number;
  dm_pct?: number | null;
  /** Cost of one stock unit in the ranch currency (decimal, not minor units). */
  cost_per_unit?: number | null;
  /** Pounds in one stock unit when the unit is not a weight (a bale, a bag). */
  unit_weight_lb?: number | null;
}

/** An ingredient line frozen into an immutable ration version. */
export interface IngredientLine extends IngredientInput {
  dm_pct: number | null;
  cost_per_unit: number | null;
  unit_weight_lb: number | null;
  /** Share of the as-fed mix, 0-100. */
  as_fed_pct: number;
  /** Share of the mix's dry matter, 0-100, or null when dry matter is unknown. */
  dm_share_pct: number | null;
  /** Ranch currency per as-fed pound, or null when the cost is unknown. */
  cost_per_lb: number | null;
}

export interface MixResult {
  lines: IngredientLine[];
  /** Sum of the entered percentages. */
  total_pct: number;
  /** Dry matter of the whole as-fed mix, or null when any ingredient's dry matter is unknown. */
  dm_pct: number | null;
  /** Ranch currency per as-fed pound, or null when any cost is unknown. */
  cost_per_lb: number | null;
  issues: Array<{ path: string; message: string }>;
}

/** Tolerance for the ingredient percentages adding to 100. */
export const PCT_TOLERANCE = 0.5;

/** Pounds in one stock unit, or null when the unit is a count without a known weight. */
export function lbPerUnit(unit: string, unitWeightLb: number | null | undefined): number | null {
  if (isWeightUnit(unit)) return convertUnit(1, unit, "lb");
  return unitWeightLb && unitWeightLb > 0 ? unitWeightLb : null;
}

/**
 * Work out a ration's as-fed and dry-matter shares, mix dry matter and cost. Dry-matter basis needs
 * every ingredient's dry matter; a missing value blocks the conversion instead of being guessed.
 */
export function mixRation(basis: RationBasis, inputs: IngredientInput[]): MixResult {
  const issues: MixResult["issues"] = [];
  const total = inputs.reduce((n, i) => n + (Number.isFinite(i.pct) ? i.pct : 0), 0);
  if (!inputs.length) issues.push({ path: "ingredients", message: "Add at least one ingredient" });
  if (inputs.length && Math.abs(total - 100) > PCT_TOLERANCE) issues.push({ path: "ingredients", message: "Ingredients must add up to 100%" });
  const seen = new Set<string>();
  inputs.forEach((i, n) => {
    if (seen.has(i.item_id)) issues.push({ path: `ingredients.${n}.item_id`, message: "This ingredient is listed twice" });
    seen.add(i.item_id);
    if (!(i.pct > 0)) issues.push({ path: `ingredients.${n}.pct`, message: "Enter a percentage above zero" });
    if (i.dm_pct !== null && i.dm_pct !== undefined && (i.dm_pct <= 0 || i.dm_pct > 100)) issues.push({ path: `ingredients.${n}.dm_pct`, message: "Dry matter must be between 0 and 100%" });
    if (basis === "dry_matter" && !(i.dm_pct && i.dm_pct > 0)) issues.push({ path: `ingredients.${n}.dm_pct`, message: "Dry matter is needed for a dry-matter ration" });
    if (!isWeightUnit(i.unit) && !(i.unit_weight_lb && i.unit_weight_lb > 0)) issues.push({ path: `ingredients.${n}.unit_weight_lb`, message: "Say how much one unit weighs" });
  });
  const dmKnown = inputs.every((i) => i.dm_pct && i.dm_pct > 0);
  // As-fed pounds per 100 units of the entered basis.
  const asFedParts = inputs.map((i) => {
    if (basis === "as_fed") return Math.max(i.pct, 0);
    return i.dm_pct && i.dm_pct > 0 ? Math.max(i.pct, 0) / (i.dm_pct / 100) : 0;
  });
  const asFedTotal = asFedParts.reduce((a, b) => a + b, 0);
  const dmParts = inputs.map((i, n) => (i.dm_pct && i.dm_pct > 0 ? asFedParts[n] * (i.dm_pct / 100) : 0));
  const dmTotal = dmParts.reduce((a, b) => a + b, 0);
  const lines: IngredientLine[] = inputs.map((i, n) => {
    const perUnit = lbPerUnit(i.unit, i.unit_weight_lb);
    const cost = i.cost_per_unit !== null && i.cost_per_unit !== undefined && perUnit ? i.cost_per_unit / perUnit : null;
    return {
      ...i,
      dm_pct: i.dm_pct ?? null,
      cost_per_unit: i.cost_per_unit ?? null,
      unit_weight_lb: i.unit_weight_lb ?? null,
      as_fed_pct: asFedTotal > 0 ? (asFedParts[n] / asFedTotal) * 100 : 0,
      dm_share_pct: dmKnown && dmTotal > 0 ? (dmParts[n] / dmTotal) * 100 : null,
      cost_per_lb: cost,
    };
  });
  const dm = dmKnown && asFedTotal > 0 ? (dmTotal / asFedTotal) * 100 : null;
  const costKnown = lines.every((l) => l.cost_per_lb !== null);
  const costPerLb = costKnown && lines.length ? lines.reduce((n, l) => n + (l.as_fed_pct / 100) * (l.cost_per_lb ?? 0), 0) : null;
  return { lines, total_pct: total, dm_pct: dm, cost_per_lb: costPerLb, issues };
}

/** As-fed pounds of each ingredient in a given as-fed amount of the mix. */
export function splitAsFed(lines: Array<Pick<IngredientLine, "item_id" | "as_fed_pct">>, asFedLb: number): Array<{ item_id: string; lb: number }> {
  return lines.map((l) => ({ item_id: l.item_id, lb: (asFedLb * l.as_fed_pct) / 100 }));
}

/** Target as-fed pounds of each ingredient for a batch, given the batch's as-fed size. */
export function loadTargets(lines: Array<Pick<IngredientLine, "item_id" | "as_fed_pct">>, batchLb: number): Array<{ item_id: string; lb: number }> {
  return splitAsFed(lines, batchLb);
}

/** Convert pounds of an ingredient into its stock unit (bales, bags, tons...). */
export function lbToStockUnits(lb: number, unit: string, unitWeightLb: number | null | undefined): number | null {
  const per = lbPerUnit(unit, unitWeightLb);
  return per ? lb / per : null;
}

// ---------------------------------------------------------------- head ledgers

/**
 * One change in a lot's head count. Arrivals count from their date; deaths, shipments and moves out
 * stop counting on their date (start-of-day convention), so a head shipped on the 6th was on feed
 * through the 5th.
 */
export interface LedgerEntry {
  on: string;
  pen_id: string | null;
  delta: number;
  kind: "in" | "move" | "out";
  reason?: string;
  event_id: string;
}

export function headOn(entries: LedgerEntry[], date: string, penId?: string | null): number {
  let n = 0;
  for (const e of entries) if (e.on <= date && (penId === undefined || e.pen_id === penId)) n += e.delta;
  return n;
}

/** Head days from `from` through `through`, both inclusive. */
export function headDays(entries: LedgerEntry[], from: string, through: string, penId?: string | null): number {
  if (through < from) return 0;
  const sorted = entries.filter((e) => penId === undefined || e.pen_id === penId).sort((a, b) => a.on.localeCompare(b.on));
  let head = 0;
  let i = 0;
  while (i < sorted.length && sorted[i].on < from) head += sorted[i++].delta;
  let total = 0;
  for (let day = from; day <= through; day = addDays(day, 1)) {
    while (i < sorted.length && sorted[i].on <= day) head += sorted[i++].delta;
    total += Math.max(head, 0);
  }
  return total;
}

/** Head by pen on a date, leaving out empty pens. */
export function headByPen(entries: LedgerEntry[], date: string): Record<string, number> {
  const out: Record<string, number> = {};
  for (const e of entries) if (e.on <= date && e.pen_id) out[e.pen_id] = (out[e.pen_id] ?? 0) + e.delta;
  for (const k of Object.keys(out)) if (out[k] === 0) delete out[k];
  return out;
}

// ---------------------------------------------------------------- allocation

/**
 * Split an amount in minor units by shares so the parts add back exactly to the total (largest
 * remainder). Shares can be percentages, head counts or any weights.
 */
export function allocateMinor(total: number, shares: number[]): number[] {
  const sum = shares.reduce((a, b) => a + Math.max(b, 0), 0);
  if (!shares.length || sum <= 0) return shares.map(() => 0);
  const raw = shares.map((s) => (total * Math.max(s, 0)) / sum);
  const floors = raw.map((r) => Math.floor(r));
  let left = Math.round(total - floors.reduce((a, b) => a + b, 0));
  const order = raw.map((r, i) => ({ i, rem: r - Math.floor(r) })).sort((a, b) => b.rem - a.rem || a.i - b.i);
  for (const o of order) {
    if (left <= 0) break;
    floors[o.i] += 1;
    left -= 1;
  }
  return floors;
}

// ---------------------------------------------------------------- closeouts

export interface CloseoutInput {
  /** Each arrival: date, head and pay weight (total pounds) when known. */
  receipts: Array<{ on: string; head: number; pay_lb_total: number | null }>;
  /** Each departure: date, head, reason and pay weight (total pounds) for shipments when known. */
  outs: Array<{ on: string; head: number; reason: string; pay_lb_total: number | null }>;
  /** Feed allocated to the lot over the period. */
  feed_as_fed_lb: number;
  /** Null when some delivered ration had no known dry matter. */
  feed_dm_lb: number | null;
  head_days: number;
  /** Money in minor units; null when unknown or not visible to the reader. */
  feed_cost_minor: number | null;
  yardage_minor: number | null;
  other_cost_minor: number | null;
}

export interface CloseoutResult {
  head_in: number;
  head_shipped: number;
  head_died: number;
  head_other_out: number;
  death_loss_pct: number | null;
  /** Head-weighted average arrival date. */
  in_on: string | null;
  /** Head-weighted days on feed of the shipped head. */
  days_on_feed: number | null;
  in_weight_avg_lb: number | null;
  out_weight_avg_lb: number | null;
  gain_total_lb: number | null;
  adg_lb: number | null;
  intake_as_fed_lb: number | null;
  intake_dm_lb: number | null;
  feed_to_gain: number | null;
  dm_to_gain: number | null;
  feed_cost_of_gain_minor_cwt: number | null;
  total_cost_minor: number | null;
  total_cost_of_gain_minor_cwt: number | null;
  /** Plain reasons a figure is blank, as English catalog keys. */
  notes: string[];
}

const dayNumber = (date: string) => Date.parse(`${date.slice(0, 10)}T12:00:00Z`) / 86_400_000;
const fromDayNumber = (n: number) => new Date(Math.round(n) * 86_400_000).toISOString().slice(0, 10);

/**
 * Lot closeout arithmetic. Days on feed run from the head-weighted arrival date to each shipment.
 * Gain is shipped pay weight out minus those head's share of pay weight in, so deads add feed but no
 * gain. Feed:gain and cost of gain are blank when gain is zero or negative, never a made-up number.
 */
export function closeoutMath(input: CloseoutInput): CloseoutResult {
  const notes: string[] = [];
  const headIn = input.receipts.reduce((n, r) => n + r.head, 0);
  const shipped = input.outs.filter((o) => o.reason === "shipped");
  const headShipped = shipped.reduce((n, o) => n + o.head, 0);
  const headDied = input.outs.filter((o) => o.reason === "died").reduce((n, o) => n + o.head, 0);
  const headOther = input.outs.filter((o) => o.reason !== "shipped" && o.reason !== "died").reduce((n, o) => n + o.head, 0);
  const inDay = headIn > 0 ? input.receipts.reduce((n, r) => n + dayNumber(r.on) * r.head, 0) / headIn : null;
  const dof = inDay !== null && headShipped > 0 ? shipped.reduce((n, o) => n + (dayNumber(o.on) - inDay) * o.head, 0) / headShipped : null;
  if (headShipped === 0) notes.push("No head shipped yet, so there is no gain to measure.");

  const inWeightsKnown = input.receipts.length > 0 && input.receipts.every((r) => r.pay_lb_total !== null);
  const inAvg = inWeightsKnown && headIn > 0 ? input.receipts.reduce((n, r) => n + (r.pay_lb_total ?? 0), 0) / headIn : null;
  if (!inWeightsKnown) notes.push("Pay weight in is missing on a receipt.");
  const outWeightsKnown = shipped.length > 0 && shipped.every((o) => o.pay_lb_total !== null);
  const outAvg = outWeightsKnown && headShipped > 0 ? shipped.reduce((n, o) => n + (o.pay_lb_total ?? 0), 0) / headShipped : null;
  if (shipped.length > 0 && !outWeightsKnown) notes.push("Pay weight out is missing on a shipment.");

  const gain = inAvg !== null && outAvg !== null ? (outAvg - inAvg) * headShipped : null;
  const adg = inAvg !== null && outAvg !== null && dof && dof > 0 ? (outAvg - inAvg) / dof : null;
  const positiveGain = gain !== null && gain > 0 ? gain : null;
  if (gain !== null && gain <= 0) notes.push("Gain is zero or negative, so feed:gain and cost of gain can't be figured.");

  const intake = input.head_days > 0 ? input.feed_as_fed_lb / input.head_days : null;
  const intakeDm = input.head_days > 0 && input.feed_dm_lb !== null ? input.feed_dm_lb / input.head_days : null;
  if (input.feed_dm_lb === null) notes.push("Dry matter is unknown for some feed, so dry-matter figures are blank.");
  const costs = [input.feed_cost_minor, input.yardage_minor, input.other_cost_minor];
  const totalCost = input.feed_cost_minor === null ? null : costs.reduce<number>((n, c) => n + (c ?? 0), 0);
  return {
    head_in: headIn,
    head_shipped: headShipped,
    head_died: headDied,
    head_other_out: headOther,
    death_loss_pct: headIn > 0 ? (headDied / headIn) * 100 : null,
    in_on: inDay !== null ? fromDayNumber(inDay) : null,
    days_on_feed: dof,
    in_weight_avg_lb: inAvg,
    out_weight_avg_lb: outAvg,
    gain_total_lb: gain,
    adg_lb: adg,
    intake_as_fed_lb: intake,
    intake_dm_lb: intakeDm,
    feed_to_gain: positiveGain ? input.feed_as_fed_lb / positiveGain : null,
    dm_to_gain: positiveGain && input.feed_dm_lb !== null ? input.feed_dm_lb / positiveGain : null,
    feed_cost_of_gain_minor_cwt: positiveGain && input.feed_cost_minor !== null ? (input.feed_cost_minor / positiveGain) * 100 : null,
    total_cost_minor: totalCost,
    total_cost_of_gain_minor_cwt: positiveGain && totalCost !== null ? (totalCost / positiveGain) * 100 : null,
    notes,
  };
}

/** Days on feed for a lot still being fed, through a date (inclusive). */
export function daysOnFeed(inOn: string, through: string): number {
  return Math.max(daysBetween(inOn, through) + 1, 0);
}

/** Percent difference of delivered against called, or null without a call. */
export function variancePct(called: number | null | undefined, delivered: number): number | null {
  if (!called || called <= 0) return null;
  return ((delivered - called) / called) * 100;
}
